import { Loading, Stack, Typography } from '@ui-components/neo.tax';
import React, { ReactElement, useMemo } from 'react';
import { ListRange } from 'react-virtuoso';

import { VirtualizedGroupList } from './sticky-headered-list.styled';

export type GroupedVirtualizedListProps<Item> = {
  sections: {
    title: string;
    items: Item[];
  }[];
  loadMore: () => void | PromiseLike<void>;
  MemoizedRowComponent: (props: { index: number; item: Item }) => ReactElement | null;
  MemoizedSectionHeaderComponent: (props: { index: number; title: string; items: Item[] }) => ReactElement | null;
  isLoading: boolean;
  atBottomThreshold?: number;
  Footer?: () => ReactElement;
  loadMoreRowFromBottomThreshold?: number;
};

export const GroupedVirtualizedList = <Item,>({
  Footer,
  MemoizedRowComponent,
  MemoizedSectionHeaderComponent,
  isLoading,
  loadMore,
  loadMoreRowFromBottomThreshold = 10,
  sections,
}: GroupedVirtualizedListProps<Item>): React.ReactElement => {
  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const groupCounts = useMemo(() => sections.map((s) => s.items.length), [sections]);

  const handleRangeChanged = (range: ListRange): void => {
    const { endIndex } = range;
    if (allItems.length - endIndex <= loadMoreRowFromBottomThreshold) {
      loadMore?.();
    }
  };

  const ShownFooter = (): ReactElement => (
    <Stack>{isLoading ? <Loading height={160} /> : Footer ? <Footer /> : <Typography>End of list</Typography>}</Stack>
  );

  return (
    <VirtualizedGroupList
      style={{ height: '100%' }}
      groupCounts={groupCounts}
      groupContent={(index): ReactElement => {
        const { items, title } = sections[index];
        return <MemoizedSectionHeaderComponent index={index} title={title} items={items} />;
      }}
      itemContent={(index): ReactElement => <MemoizedRowComponent index={index} item={allItems[index]} />}
      rangeChanged={handleRangeChanged}
      components={{ Footer: ShownFooter }}
      endReached={loadMore}
    />
  );
};
